import type {
  EPaymentMethod,
  GetApiV1IndividualpaymentPeoplePersonidCoursesQueryParams,
  GetApiV1IndividualpaymentPeopleQueryParams,
} from '@/shared/api/generated';

import type { IndividualPaymentFilters } from '../types/individualPayment';

/** Valores válidos de `EPaymentMethod` no spec (1..7). */
const PAYMENT_METHOD_VALUES: readonly EPaymentMethod[] = [1, 2, 3, 4, 5, 6, 7];

/** Retorna o texto sem espaços nas pontas, ou `undefined` se vazio. */
function text(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Converte um id de lookup para número, ou `undefined` se vazio/inválido.
 *
 * Exportada porque é o mesmo critério usado por `hasAnyIndividualPaymentFilter`
 * para habilitar o botão "Pesquisar": se o id não converte aqui, ele não vai na
 * query, e habilitar a busca produziria uma pesquisa sem o filtro.
 *
 * @param value - Valor cru do filtro (o `value` selecionado no autocomplete).
 */
export function parseLookupId(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Estreita o valor do select para `EPaymentMethod`.
 *
 * O select trabalha com string; um valor fora de 1..7 (backend novo, filtro
 * corrompido) é descartado em vez de enviado como método inválido.
 */
function paymentMethod(value: string): EPaymentMethod | undefined {
  const parsed = parseLookupId(value);
  return PAYMENT_METHOD_VALUES.find(method => method === parsed);
}

/**
 * Traduz os filtros da tela para os query params de
 * `GET /api/v1/individualpayment/people`.
 *
 * Campos vazios são **omitidos** em vez de enviados como string vazia, para não
 * transformar "sem filtro" em "filtro por vazio" no backend. A montagem é por
 * atribuição condicional (e não por spread) para que, com
 * `exactOptionalPropertyTypes`, nenhuma propriedade opcional receba `undefined`.
 *
 * **Filtro de aluno:** a seleção no lookup alimenta três params, todos derivados
 * do que `person/shared/people-multiple` devolve — `PersonIds` (dígitos da chave
 * `"USR260953"`, já sem o prefixo), `StudentName` e `Email` (nome e e-mail do
 * rótulo `"Nome  [email]"`). Sem seleção, só o nome digitado vai em
 * `StudentName`. O `Email` do painel avançado, digitado explicitamente pelo
 * operador, tem precedência sobre o e-mail derivado da seleção.
 *
 * @param filters - Filtros aplicados.
 * @param page - Página 1-based.
 * @param pageSize - Itens por página.
 *
 * @example
 * ```ts
 * buildPeopleQueryParams({ ...filters, cpf: '123' }, 1, 50);
 * // { PageNumber: 1, PageSize: 50, Cpf: '123' }
 * ```
 */
export function buildPeopleQueryParams(
  filters: IndividualPaymentFilters,
  page: number,
  pageSize: number,
): GetApiV1IndividualpaymentPeopleQueryParams {
  const params: GetApiV1IndividualpaymentPeopleQueryParams = {
    PageNumber: page,
    PageSize: pageSize,
  };

  const personId = parseLookupId(filters.personId);
  const companyId = parseLookupId(filters.companyId);
  const classStudentId = parseLookupId(filters.classStudentId);
  const method = paymentMethod(filters.paymentMethod);
  const ra = text(filters.ra);
  const cpf = text(filters.cpf);
  const studentName = text(filters.studentName);
  const email = text(filters.email) ?? text(filters.studentEmail);

  if (personId !== undefined) params.PersonIds = [personId];
  if (studentName !== undefined) params.StudentName = studentName;
  if (companyId !== undefined) params.CompanyId = companyId;
  if (classStudentId !== undefined) params.ClassStudentId = classStudentId;
  if (method !== undefined) params.PaymentMethod = method;
  if (ra !== undefined) params.Ra = ra;
  if (cpf !== undefined) params.Cpf = cpf;
  if (email !== undefined) params.Email = email;

  const billing = buildContractQueryParams(filters);
  if (billing.OurNumber !== undefined) params.OurNumber = billing.OurNumber;
  if (billing.InvoiceNumber !== undefined) params.InvoiceNumber = billing.InvoiceNumber;
  if (billing.ThirdPartyCpf !== undefined) params.ThirdPartyCpf = billing.ThirdPartyCpf;
  if (billing.ThirdPartyCnpj !== undefined) params.ThirdPartyCnpj = billing.ThirdPartyCnpj;

  return params;
}

/**
 * Query params de cobrança — os quatro filtros aceitos tanto por `/people`
 * quanto por `/people/{personId}/courses`.
 *
 * Repassá-los às linhas-filhas é obrigatório: sem isso a subtabela exibiria
 * contratos que não casam com a pesquisa feita na linha-pai.
 *
 * @param filters - Filtros aplicados, ou `null` quando nada foi pesquisado.
 */
export function buildContractQueryParams(
  filters: IndividualPaymentFilters | null,
): GetApiV1IndividualpaymentPeoplePersonidCoursesQueryParams {
  const params: GetApiV1IndividualpaymentPeoplePersonidCoursesQueryParams = {};
  if (filters === null) return params;

  const ourNumber = text(filters.ourNumber);
  const invoiceNumber = text(filters.invoiceNumber);
  const thirdPartyCpf = text(filters.thirdPartyCpf);
  const thirdPartyCnpj = text(filters.thirdPartyCnpj);

  if (ourNumber !== undefined) params.OurNumber = ourNumber;
  if (invoiceNumber !== undefined) params.InvoiceNumber = invoiceNumber;
  if (thirdPartyCpf !== undefined) params.ThirdPartyCpf = thirdPartyCpf;
  if (thirdPartyCnpj !== undefined) params.ThirdPartyCnpj = thirdPartyCnpj;

  return params;
}
